// The Moon's real heights: LOLA elevation tiles (see src/data/moonImagerySource.js)
// for physics, the terrain mesh and the shadow height maps, plus the WAC
// albedo map the crater field reads its terrain age from.
//
// Heights are sampled synchronously from whatever is loaded: the finest tile
// covering the point at or coarser than the level the caller's feature size
// asks for. Asking also queues a load of that tile, and when tiles arrive
// listeners hear about it (terrain patches rebuild, landed craft re-seat). The
// global base levels (L0-L2) load first, so there is always an answer once
// loadMoonBase() has resolved.
//
// Tiles come from the project (public/textures/moon-tiles/lola, the dev
// server fills it on demand) and, in a static deploy that did not ship a
// detail tile, levels 6-7 are built in the browser from the 118 m mosaic with
// byte-range requests. In Node (headless tests) setMoonDemReader() supplies a
// file reader, since there is no dev server.
import {
  MOON_DEM_PATH, MOON_DEM_MAX_LEVEL, MOON_DEM_POSTS, MOON_RADIUS, LDEM_118M_URL, LDEM_118M,
  moonSpan, decodeHeight, demTileFrom118m
} from '../data/moonImagerySource.js';
import { decodePng } from './pngdata.js';

const N = MOON_DEM_POSTS;
const BASE_LEVEL = 2;
const tiles = new Map();          // key -> Float32Array
const loading = new Map();        // key -> Promise
const failed = new Set();
const listeners = new Set();
let reader = null;                // async (relativePath) => Uint8Array | null
let albedo = null;                // { w, h, data: Uint8Array }
let baseReady = false;
let notifyTimer = null;
let dirty = [];
const queue = [];
let active = 0;
const MAX_ACTIVE = 3;

export const moonDemStats = { loaded: 0, failed: 0, pending: 0, maxLevel: 0 };

// Post spacing (m) of level z.
export const demSpacing = (z) => MOON_RADIUS * moonSpan(z) * Math.PI / 180 / 256;

// Coarsest level whose posts are at most `feature` / 2.5 apart (the terrain's
// rule for what a grid can draw), clamped to the data.
let levelCap = MOON_DEM_MAX_LEVEL;
export function demLevelFor(feature) {
  for (let z = 0; z <= levelCap; z++) if (demSpacing(z) * 2.5 <= feature) return z;
  return levelCap;
}
// Finest DEM level used (quality tiers, diagnostics).
export function setMoonDemCap(z) { levelCap = Math.max(BASE_LEVEL, Math.min(MOON_DEM_MAX_LEVEL, z)); }

export function setMoonDemReader(fn) { reader = fn; }
export function onMoonDem(fn) { listeners.add(fn); return () => listeners.delete(fn); }
export const moonDemReady = () => baseReady;

function tileKey(z, x, y) { return `${z}/${x}/${y}`; }

async function readBytes(relative) {
  if (reader) return reader(relative);
  const res = await fetch(`${MOON_DEM_PATH}/${relative}`);
  if (!res.ok || !/^image\//.test(res.headers.get('content-type') || '')) return null;
  return new Uint8Array(await res.arrayBuffer());
}

// In a browser without the tile: build L6/L7 from the 118 m mosaic.
async function browserRow(row, col0, count) {
  const W = LDEM_118M.width, out = new Int16Array(count);
  const c = ((col0 % W) + W) % W, first = Math.min(count, W - c);
  let k = 0;
  for (const [start, n] of [[c, first], [0, count - first]]) {
    if (n <= 0) continue;
    const offset = LDEM_118M.row0 + row * W * 2 + start * 2;
    const res = await fetch(LDEM_118M_URL, { headers: { Range: `bytes=${offset}-${offset + n * 2 - 1}` } });
    if (res.status !== 206) throw new Error('DEM range ' + res.status);
    const v = new DataView(await res.arrayBuffer());
    for (let i = 0; i < n; i++) out[k++] = v.getInt16(i * 2, true);
  }
  return out;
}

async function loadTile(z, x, y) {
  const key = tileKey(z, x, y);
  const bytes = await readBytes(`${z}/${y}/${x}.png`).catch(() => null);
  let heights = null;
  if (bytes) {
    const img = await decodePng(bytes);
    heights = new Float32Array(N * N);
    const c = img.channels, d = img.data;
    for (let i = 0; i < N * N; i++) heights[i] = decodeHeight(d[i * c], d[i * c + 1], d[i * c + 2]);
  } else if (!reader && z >= 6 && typeof fetch === 'function') {
    heights = await demTileFrom118m(z, x, y, browserRow, 6);
  }
  if (!heights) throw new Error('No DEM tile ' + key);
  tiles.set(key, heights);
  moonDemStats.loaded = tiles.size;
  moonDemStats.maxLevel = Math.max(moonDemStats.maxLevel, z);
  return { z, x, y };
}

function pump() {
  while (active < MAX_ACTIVE && queue.length) {
    const { z, x, y, resolve } = queue.shift();
    const key = tileKey(z, x, y);
    active++;
    const p = loadTile(z, x, y)
      .then((t) => { dirty.push(t); scheduleNotify(); resolve(true); })
      .catch(() => { failed.add(key); moonDemStats.failed++; resolve(false); })
      .finally(() => { active--; loading.delete(key); moonDemStats.pending = loading.size; pump(); });
    loading.set(key, p);
  }
  moonDemStats.pending = loading.size + queue.length;
}

export function requestMoonDemTile(z, x, y) {
  const key = tileKey(z, x, y);
  if (tiles.has(key) || failed.has(key)) return Promise.resolve(tiles.has(key));
  if (loading.has(key)) return loading.get(key);
  const existing = queue.find(q => q.key === key);
  if (existing) return existing.promise;
  let resolve;
  const promise = new Promise(r => { resolve = r; });
  // Finer tiles first: they are what the camera is waiting for.
  queue.push({ key, z, x, y, resolve, promise });
  queue.sort((a, b) => b.z - a.z);
  pump();
  return promise;
}

function scheduleNotify() {
  if (notifyTimer) return;
  const fire = () => {
    notifyTimer = null;
    const batch = dirty; dirty = [];
    for (const fn of listeners) fn(batch);
  };
  notifyTimer = typeof setTimeout === 'function' ? setTimeout(fire, 250) : (fire(), null);
}

export async function loadMoonBase() {
  const jobs = [];
  for (let z = 0; z <= BASE_LEVEL; z++) for (let y = 0; y < 2 ** z; y++) for (let x = 0; x < 2 ** (z + 1); x++) jobs.push(requestMoonDemTile(z, x, y));
  jobs.push(loadAlbedo());
  await Promise.all(jobs);
  baseReady = true;
}

// Every tile covering a disc of `radius` metres around (lat, lon) degrees, at
// `level` and every coarser level down to the base. Resolves when loaded.
export function requestMoonDemArea(lat, lon, radius, level = MOON_DEM_MAX_LEVEL) {
  const jobs = [];
  const dLat = radius / MOON_RADIUS * 180 / Math.PI;
  const dLon = dLat / Math.max(0.05, Math.cos(lat * Math.PI / 180));
  for (let z = BASE_LEVEL + 1; z <= level; z++) {
    const span = moonSpan(z);
    const x0 = Math.floor((lon - dLon + 180) / span), x1 = Math.floor((lon + dLon + 180) / span);
    const y0 = Math.max(0, Math.floor((90 - lat - dLat) / span)), y1 = Math.min(2 ** z - 1, Math.floor((90 - lat + dLat) / span));
    for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) {
      const w = 2 ** (z + 1);
      jobs.push(requestMoonDemTile(z, ((x % w) + w) % w, y));
    }
  }
  return Promise.all(jobs);
}

function sampleTile(t, z, fx, fy) {
  // fx, fy: post coordinates inside the tile, 0..256.
  const i = Math.min(255, Math.floor(fx)), j = Math.min(255, Math.floor(fy));
  const ax = fx - i, ay = fy - j;
  const a = t[j * N + i], b = t[j * N + i + 1], c = t[(j + 1) * N + i], d = t[(j + 1) * N + i + 1];
  return (a * (1 - ax) + b * ax) * (1 - ay) + (c * (1 - ax) + d * ax) * ay;
}

// Height (m above 1737.4 km) at lat/lon radians. `feature` (m) is the finest
// relief the caller can use; 0 asks for full detail. `request` queues the
// wanted tile when it is not loaded. Returns { h, level }.
const _out = { h: 0, level: -1 };
export function moonDemHeight(lat, lon, feature = 0, request = true) {
  const latD = lat * 180 / Math.PI, lonD = ((lon * 180 / Math.PI + 180) % 360 + 360) % 360 - 180;
  const want = demLevelFor(feature);
  for (let z = want; z >= 0; z--) {
    const span = moonSpan(z);
    const gx = (lonD + 180) / span, gy = Math.min(2 ** z - 1e-9, Math.max(0, (90 - latD) / span));
    const x = Math.min(2 ** (z + 1) - 1, Math.floor(gx)), y = Math.floor(gy);
    const t = tiles.get(tileKey(z, x, y));
    if (!t) {
      if (request && z === want && z > BASE_LEVEL) requestMoonDemTile(z, x, y);
      continue;
    }
    _out.h = sampleTile(t, z, (gx - x) * 256, (gy - y) * 256);
    _out.level = z;
    return _out;
  }
  _out.h = 0; _out.level = -1;
  return _out;
}

// --- WAC albedo map ------------------------------------------------------------
// public/textures/moon-tiles/albedo.png: 2048 x 1024 grey, equirectangular,
// normal albedo = value / 255 * ALBEDO_SCALE (built by scripts/build-moon-tiles.mjs).
export const MOON_ALBEDO_URL = '/textures/moon-tiles/albedo.png';
export const ALBEDO_SCALE = 0.5;

async function loadAlbedo() {
  if (albedo) return;
  try {
    const bytes = reader ? await reader('../albedo.png') : await fetch(MOON_ALBEDO_URL).then(r => r.ok ? r.arrayBuffer() : null).then(b => b && new Uint8Array(b));
    if (!bytes) return;
    const img = await decodePng(bytes);
    const data = new Uint8Array(img.width * img.height);
    for (let i = 0; i < data.length; i++) data[i] = img.data[i * img.channels];
    albedo = { w: img.width, h: img.height, data };
  } catch { /* craters fall back to highland statistics */ }
}

// Normal albedo at lat/lon radians (bilinear); null before the map loads.
export function moonAlbedo(lat, lon) {
  if (!albedo) return null;
  const { w, h, data } = albedo;
  const fx = (((lon * 180 / Math.PI + 180) % 360 + 360) % 360) / 360 * w - 0.5;
  const fy = Math.max(0, Math.min(h - 1.001, (90 - lat * 180 / Math.PI) / 180 * h - 0.5));
  const x = Math.floor(fx), y = Math.floor(fy), ax = fx - x, ay = fy - y;
  const p = (i, j) => data[j * w + ((i % w) + w) % w];
  const v = (p(x, y) * (1 - ax) + p(x + 1, y) * ax) * (1 - ay) + (p(x, y + 1) * (1 - ax) + p(x + 1, y + 1) * ax) * ay;
  return v / 255 * ALBEDO_SCALE;
}
